// src/components/chat/PlaybookCitationBubble.tsx

/**
 * PlaybookCitationBubble
 *
 * Inline chip for playbook citations. Same neutral chip style as
 * DocumentCitationBubble for visual consistency; the book icon distinguishes
 * it from regular document citations. Slim payload comes from
 * src/tools/playbook/citation.py.
 *
 * With `groupRefs` the chip stands for several adjacent playbook citations:
 * it shows the first one's label plus "+N", pages through all of them in the
 * tooltip, and can be pinned open by click, tap or Enter.
 */

import React, { useCallback, useRef } from "react";
import { useDocumentCitations } from "../../contexts/DocumentCitationContext";
import type { PlaybookCitationTooltipData } from "../../contexts/PlaybookCitationTooltipContext";
import { usePlaybookCitationTooltip } from "../../contexts/PlaybookCitationTooltipContext";
import type { PlaybookCitation } from "../../interfaces/documentCitationTypes";
import { isPlaybookCitation } from "../../interfaces/documentCitationTypes";
import { PlaybookIcon } from "./DocumentCitationIcons";
import { CitationCountBadge } from "./CitationGroupControls";
import { useCitationGroupChip } from "./hooks/useCitationGroupChip";

export interface PlaybookCitationBubbleProps {
  /** Reference matching the <doc-cite data-ref="REF" /> tag */
  citationRef: string;
  /** Refs of a merged run of playbook citations, `citationRef` first. Absent for a single citation. */
  groupRefs?: string[];
}

const labelOf = (citation: PlaybookCitation): string =>
  citation.breadcrumb.length > 0
    ? citation.breadcrumb[citation.breadcrumb.length - 1]
    : citation.document_title;

const toTooltipData = (citation: PlaybookCitation): PlaybookCitationTooltipData => ({
  documentTitle: citation.document_title,
  breadcrumb: citation.breadcrumb,
  importance: citation.importance,
  citedText: citation.cited_text,
  sources: citation.sources,
});

export const PlaybookCitationBubble: React.FC<PlaybookCitationBubbleProps> = ({
  citationRef,
  groupRefs,
}) => {
  const { lookupCitation } = useDocumentCitations();
  const { state, showTooltip, hideTooltip, showTooltipGroup, pageTooltip, closeTooltip } =
    usePlaybookCitationTooltip();
  const bubbleRef = useRef<HTMLSpanElement>(null);

  const citation = lookupCitation(citationRef);
  const groupCitations = (groupRefs ?? [])
    .map(lookupCitation)
    .filter((c): c is PlaybookCitation => !!c && isPlaybookCitation(c));
  const isGroup = groupCitations.length > 1;
  const isOpen = state.isVisible && !!state.group && state.group.anchorEl === bubbleRef.current;

  const openGroup = useCallback(
    (pinned: boolean) => {
      if (!bubbleRef.current) return;
      showTooltipGroup(bubbleRef.current, groupCitations.map(toTooltipData), pinned);
    },
    [showTooltipGroup, groupCitations]
  );

  const groupChipProps = useCitationGroupChip({
    isGroup,
    isOpen,
    isPinned: state.pinned,
    open: openGroup,
    close: closeTooltip,
    page: pageTooltip,
  });

  const handleMouseEnter = useCallback(() => {
    if (isGroup) {
      openGroup(false);

      return;
    }
    const cit = lookupCitation(citationRef);
    if (!cit || !isPlaybookCitation(cit)) return;
    const rect = bubbleRef.current?.getBoundingClientRect() ?? null;
    showTooltip({
      anchorRect: rect,
      documentTitle: cit.document_title,
      breadcrumb: cit.breadcrumb,
      importance: cit.importance,
      citedText: cit.cited_text,
      sources: cit.sources,
    });
  }, [isGroup, openGroup, showTooltip, lookupCitation, citationRef]);

  const handleMouseLeave = useCallback(() => {
    hideTooltip();
  }, [hideTooltip]);

  // No payload for this ref: render nothing rather than invent a source label
  if (!citation) return null;
  const label =
    isPlaybookCitation(citation) && citation.breadcrumb.length > 0
      ? citation.breadcrumb[citation.breadcrumb.length - 1]
      : citation.document_title;
  const ariaLabel = isGroup
    ? `${groupCitations.length} playbook citations: ${groupCitations.map(labelOf).join(", ")}`
    : `Playbook citation: ${label}`;

  return (
    <span
      ref={bubbleRef}
      className="playbook-citation-bubble"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      {...groupChipProps}
      role="button"
      tabIndex={0}
      aria-label={ariaLabel}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        padding: "2px 8px",
        margin: "0 2px",
        backgroundColor: "#F5F5F5",
        border: "1px solid #E0E0E0",
        borderRadius: "6px",
        fontSize: "11px",
        fontWeight: 500,
        color: "#424242",
        cursor: "pointer",
        transition: "all 0.15s ease",
        whiteSpace: "nowrap",
        verticalAlign: "baseline",
      }}
      onMouseOver={(e) => {
        e.currentTarget.style.backgroundColor = "#EEEEEE";
        e.currentTarget.style.borderColor = "#90CAF9";
      }}
      onMouseOut={(e) => {
        e.currentTarget.style.backgroundColor = "#F5F5F5";
        e.currentTarget.style.borderColor = "#E0E0E0";
      }}
    >
      <PlaybookIcon size={14} color="#9E9E9E" />
      <span
        style={{
          maxWidth: "160px",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {label}
      </span>
      {isGroup && <CitationCountBadge count={groupCitations.length - 1} />}
    </span>
  );
};

export default PlaybookCitationBubble;
